import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useMemo, useState, Suspense, lazy, useRef, useEffect } from "react";
import styles from "./CategoryTabs.module.scss";
import Chevron from "icons/Chevron";
import MenuDropdown from "@Components/MenuDropdown";
import useIsDesktop from "hooks/useIsDesktop";
import HorizontalScroll from "@Components/HorizontalScroll";
import SortIcon from "@Icons/SortIcon";
import Portal from "@Components/Portal";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import FullPageLoader from "@Components/FullPageLoader";
import useTranslationHook from "hooks/useTranslationHook";

const SortOptions = lazy(() => import("@Components/SortOptions/SortOptions"));

export interface CategoryItem {
  id: number;
  name: string;
  slug: string;
}

interface CategoryTabsProps {
  categories: CategoryItem[];
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  selectedSort?: string;
  onSortChange?: (sortType: string) => void;
  showSortButton?: boolean;
  maxVisibleCategories?: number;
  isGridPage?: boolean;
}

const CategoryTabs = ({
  categories,
  activeCategory,
  onCategoryChange,
  selectedSort = "newest",
  onSortChange,
  showSortButton = false,
  maxVisibleCategories = 5,
  isGridPage = false,
}: CategoryTabsProps) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isSortOverlayOpen, setIsSortOverlayOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { visibleCategories, moreCategories } = useMemo(() => {
    const visible = ["Latest", ...categories.slice(0, maxVisibleCategories).map((cat) => cat.name)];
    const more = categories.slice(maxVisibleCategories).map((cat) => cat.name);

    return {
      visibleCategories: visible,
      moreCategories: more,
    };
  }, [categories]);

  const handleCategoryClick = (category: string) => {
    onCategoryChange(category);
    setIsMoreMenuOpen(false);
  };

  useEffect(() => {
    if (!isDesktop && scrollRef.current) {
      const scrollableElement = scrollRef.current.querySelector(
        "[class*='horizontalScroll']",
      ) as HTMLElement;
      if (scrollableElement) {
        scrollableElement.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }
    }
  }, [activeCategory, isDesktop]);

  const handleSortSelect = (sortType: string) => {
    if (onSortChange) {
      onSortChange(sortType);
    }
    setIsSortOverlayOpen(false);
  };

  const hideSortOverlay = () => {
    setIsSortOverlayOpen(false);
  };

  const isActiveCategoryInMore = moreCategories.some(
    (cat) => cat.toLowerCase() === activeCategory.toLowerCase(),
  );

  if (!isDesktop) {
    let allCategories = ["Latest", ...categories.map((cat) => cat.name)];
    if (activeCategory !== "Latest") {
      const activeIndex = allCategories.findIndex(
        (cat) => cat.toLowerCase() === activeCategory.toLowerCase(),
      );
      if (activeIndex > 0) {
        const activeCat = allCategories[activeIndex];
        allCategories = [
          activeCat,
          ...allCategories.slice(0, activeIndex),
          ...allCategories.slice(activeIndex + 1),
        ];
      }
    }
    return (
      <>
        <div className={styles.tabsContainerMobile} ref={scrollRef}>
          <HorizontalScroll className={styles.mobileScrollContainer}>
            {showSortButton && (
              <button
                className={`${styles.tab} ${styles.sortTab} ${
                  selectedSort !== "newest" ? styles.activeTab : ""
                }`}
                type="button"
                onClick={() => setIsSortOverlayOpen(true)}
              >
                <SortIcon fill={selectedSort !== "newest" ? "#ED3A56" : "#374151"} />
                <span>{t("blog.grid.sort")}</span>
              </button>
            )}
            {allCategories.map((category) => {
              const isActive = category.toLowerCase() === activeCategory.toLowerCase();
              return (
                <button
                  key={category}
                  className={`${styles.tab} ${isActive ? styles.activeTab : ""}`}
                  onClick={() => handleCategoryClick(category)}
                  type="button"
                >
                  <span>{category === "Latest" ? t("blog.grid.latest") : category}</span>
                </button>
              );
            })}
          </HorizontalScroll>
        </div>

        {isSortOverlayOpen && (
          <Portal rootId="forPortal">
            <BottomSheetOverlay linePosition="bottom" hideBottomSheetOverlay={hideSortOverlay}>
              <Suspense fallback={<FullPageLoader height="230px" width="360px" />}>
                <SortOptions
                  value={selectedSort}
                  onSortChange={handleSortSelect}
                  hideBottomSheetOverlay={hideSortOverlay}
                />
              </Suspense>
            </BottomSheetOverlay>
          </Portal>
        )}
      </>
    );
  }

  const activeCategoryLabel = activeCategory === "Latest" ? t("blog.grid.latest") : activeCategory;

  return (
    <div className={`${styles.tabsContainer} ${isGridPage ? styles.gridPage : ""}`}>
      {visibleCategories.map((category) => {
        const isActive = category.toLowerCase() === activeCategory.toLowerCase();

        return (
          <button
            key={category}
            className={`${styles.tab} ${isActive ? styles.activeTab : ""}`}
            onClick={() => handleCategoryClick(category)}
            type="button"
          >
            <span>{(category === "Latest" ? t("blog.grid.latest") : category).trim()}</span>
          </button>
        );
      })}

      {moreCategories.length > 0 && (
        <MenuDropdown
          target={
            <button
              className={`${styles.tab} ${styles.moreTab} ${
                isActiveCategoryInMore ? styles.activeTab : ""
              } ${isMoreMenuOpen ? styles.moreTabOpen : ""}`}
              type="button"
            >
              <span>{isActiveCategoryInMore ? activeCategoryLabel : t("blog.grid.more")}</span>
              <div className={styles.chevron}>
                <Chevron height="12" width="12" />
              </div>
            </button>
          }
          menu={
            <div className={styles.moreMenu}>
              {moreCategories.map((category) => {
                const isActive = category.toLowerCase() === activeCategory.toLowerCase();
                return (
                  <div
                    key={category}
                    className={`${styles.moreMenuItem} ${isActive ? styles.activeMenuItem : ""}`}
                    onClick={() => handleCategoryClick(category)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCategoryClick(category);
                    }}
                  >
                    {category === "Latest" ? t("blog.grid.latest") : category}
                  </div>
                );
              })}
            </div>
          }
          closeOnSelect
          handleOnClose={() => setIsMoreMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default wrapperHOC(memo(CategoryTabs), {
  componentName: "CategoryTabs",
  showForChina: true,
});
